import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useActor } from '@/auth/pin/phoneSession'
import { useI18n } from '@/i18n'
import { enqueueCommand } from '@/offline'
import {
  Banner,
  BilingualLabel,
  Button,
  Field,
  PhotoCapture,
  PickerSheet,
  ScanField,
  useToast,
  type ScanMeta,
} from '@/ui'
import type { BindMachineLabelPayload } from '../commands'
import '../commands'
import type { ProcessState } from '../projection'
import { checkFreshCode } from '../model/rules'
import { Line } from '../components/bits'
import { useRefusal } from '../components/useText'
import { ProcessScreen } from '../components/ProcessScreen'

/** F6.9 — Machine label: scan a new MACHINE label, pick the unlabelled machine, photograph it. */
export function MachineLabelPage() {
  return <ProcessScreen title="process.label.title">{(state) => <MachineLabel state={state} />}</ProcessScreen>
}

function MachineLabel({ state }: { state: ProcessState }) {
  const { t } = useI18n()
  const toast = useToast()
  const actor = useActor()
  const navigate = useNavigate()
  const refusal = useRefusal()
  const [code, setCode] = useState<{ code: string; entry: ScanMeta['entry'] } | null>(null)
  const [machineId, setMachineId] = useState<string | null>(null)
  const [photo, setPhoto] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const unlabelled = [...state.machines.values()].filter((m) => !m.label_payload)
  const machine = machineId ? state.machines.get(machineId) : undefined

  const bind = async () => {
    if (!code || !machine || !photo) return
    setSaving(true)
    try {
      const payload: BindMachineLabelPayload = {
        equipment_id: machine.id,
        qr_payload: code.code,
        entry: code.entry,
        photo_id: photo,
      }
      await enqueueCommand({ type: 'bind_machine_label', payload, actorId: actor.user_id, mediaIds: [photo] })
      toast.success(t('process.label.done', { code: machine.code }))
      navigate('/m/process', { replace: true })
    } catch (e) {
      toast.error(t('process.saveFailed'), e instanceof Error ? e.message : undefined)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="flex flex-col gap-4">
      {unlabelled.length === 0 && <Banner tone="info">{t('process.label.allLabelled')}</Banner>}
      <Field label={<BilingualLabel k="process.label.scan" />} required>
        <ScanField
          value={code?.code ?? null}
          sheetTitle={<BilingualLabel k="process.label.scan" />}
          validate={(c) => {
            const v = checkFreshCode(state, c, 'MACHINE', actor.user_id)
            return v.ok ? null : refusal(v.reason)
          }}
          onChange={(c, meta) => setCode(c ? { code: c.trim().toUpperCase(), entry: meta?.entry ?? 'SCAN' } : null)}
        />
      </Field>
      <Field label={<BilingualLabel k="process.label.machine" />} hint={t('process.label.machineHint')} required>
        <PickerSheet
          title={<BilingualLabel k="process.label.machine" />}
          options={unlabelled.map((m) => ({
            value: m.id,
            label: `${m.code} · ${m.name}`,
            description: m.category ?? undefined,
            searchText: `${m.code} ${m.name}`,
          }))}
          value={machineId}
          onChange={setMachineId}
          disabled={unlabelled.length === 0}
        />
      </Field>
      {machine && <Line label={t('process.label.site')} value={state.pack.site_name ?? state.pack.site_code ?? '—'} />}
      <Field label={<BilingualLabel k="process.label.photo" />} required>
        <PhotoCapture value={photo} onChange={setPhoto} />
      </Field>
      <Button size="xl" fullWidth disabled={!code || !machine || !photo} loading={saving} onClick={() => void bind()}>
        <BilingualLabel k="process.label.bind" />
      </Button>
      <Button variant="ghost" size="touch" fullWidth onClick={() => navigate(-1)}>
        <BilingualLabel k="common.cancel" />
      </Button>
    </div>
  )
}
